import React, { useState, useEffect, useMemo } from 'react';
import Image from 'next/image';
import confirmingIcon from '@/features/wallet/assets/confirming.png';
import iconUsdc from '@/features/wallet/assets/icon-usdc.png';
import iconDai from '@/features/wallet/assets/icon-dai.png';
import { type StaticImageData } from 'next/image';
import DetailRow from './detail-row';

const CRYPTO_ICONS: Record<string, StaticImageData> = {
  USDC: iconUsdc,
  DAI: iconDai,
};
import Skeleton from './skeleton';
import { useGetDigitalCurrencyDepositOrder } from '@/features/wallet/services/digital-currency-deposit-order';
import { useGetCashDepositOrder } from '@/features/wallet/services/cash-deposit-order';
import { useGetStaticResource, useGetCurrencyIcon } from '@/services/static-resource';
import { useTranslations } from 'next-intl';
import { formatSecondToDate } from '@/utils/time';
import { getBlockchainExplorerUrl } from '@/utils/blockchain-explorer';
import { useBalanceFormatter } from '@/hooks/use-balance-formatter';
import { useQueryClient } from '@tanstack/react-query';
import DelayTips from '../delay-tips';
import { CURRENCY_TYPE } from '@/const/currency';

interface DepositDetailsProps {
  order_id: string;
  type: 1 | 2; // 1: cash deposit, 2: digital currency deposit
}

const DepositDetails = ({ order_id, type }: DepositDetailsProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showRefreshToast, setShowRefreshToast] = useState(false);
  const t = useTranslations('personal');
  const queryClient = useQueryClient();

  // 获取静态资源（币种图标等）
  const { data: staticResource } = useGetStaticResource();
  const getCurrencyIcon = useGetCurrencyIcon();
  const { formatCurrencyDetail } = useBalanceFormatter();


  // 根据 type 决定使用哪个 API
  const digitalCurrencyQuery = useGetDigitalCurrencyDepositOrder({
    order_id,
    queryConfig: { enabled: type === 2 }
  });

  const cashDepositQuery = useGetCashDepositOrder({
    order_id,
    queryConfig: { enabled: type === 1 }
  });

  // 获取当前数据
  const isLoading = type === 2 ? digitalCurrencyQuery.isLoading : type === 1 ? cashDepositQuery.isLoading : false;
  const error = type === 2 ? digitalCurrencyQuery.error : type === 1 ? cashDepositQuery.error : null;

  // 显示状态的文案和颜色
  const getStatusMeta = (status?: string) => {
    switch (status) {
      case '1':
        return { label: t('history.status.completed'), className: 'text-success' };
      case '2':
        return { label: t('history.status.processing'), className: 'text-pending' };
      case '3':
        return { label: t('history.status.failed'), className: 'text-danger' };
      default:
        return { label: t('history.status.processing'), className: 'text-pending' };
    }
  };

  useEffect(() => {
    const data =
      type === 2
        ? digitalCurrencyQuery.data
        : cashDepositQuery.data;

    if (data) {
      syncOrderListStatus(data);
    }
  }, [
    type,
    digitalCurrencyQuery.data,
    cashDepositQuery.data,
  ]);


  // 刷新接口
  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      // 最小动画时长（600ms）
      const MIN_REFRESH_DURATION = 500;

      // 创建最小时长 Promise
      const minDurationPromise: Promise<void> = new Promise(resolve => {
        const timer = setTimeout(() => {
          clearTimeout(timer);
          resolve();
        }, MIN_REFRESH_DURATION);
      });

      // 创建 API 请求 Promise
      const apiPromise = type === 2
        ? digitalCurrencyQuery.refetch()
        : type === 1
          ? cashDepositQuery.refetch()
          : Promise.resolve();

      // 等待两者都完成
      await Promise.all([apiPromise, minDurationPromise]);

      // 显示刷新成功 toast
      // setShowRefreshToast(true);

      // 3秒后自动隐藏 toast
      const timer = setTimeout(() => {
        clearTimeout(timer);
        setShowRefreshToast(false);
      }, 3000);

    } catch (error) {
      console.log(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // 刷新成功后同步订单列表状态
  function syncOrderListStatus(latestOrder) {
    if (!latestOrder) return;

    // ✅ 关键：同步列表页的 infiniteQuery
    queryClient.setQueryData(
      ['transactions', 'deposit'],
      (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            list: page.list.map((item) =>
              item.order_id === latestOrder.order_id
                ? {
                  ...item,
                  status: latestOrder.status,
                  time: latestOrder.time,
                }
                : item
            ),
          })),
        };
      }
    );
  }

  const delayTime = useMemo(() => {
    let time = 0;
    if (type === 2 && digitalCurrencyQuery.data?.status === '2') {
      time = Number(digitalCurrencyQuery.data.time || 0);
    } else if (type === 1 && cashDepositQuery.data?.status === '2') {
      time = Number(cashDepositQuery.data.time || 0);
    }

    return time;
  }, [cashDepositQuery.data, digitalCurrencyQuery.data, type]);

  const currency = useMemo(() => {
    let currency = '';
    if (type === 2 && digitalCurrencyQuery.data) {
      currency = digitalCurrencyQuery.data.currency || '';
    } else if (type === 1 && cashDepositQuery.data) {
      currency = cashDepositQuery.data.currency || '';
    }

    return currency;
  }, [cashDepositQuery.data, digitalCurrencyQuery.data, type]);

  // 如果正在加载，显示骨架屏
  if (isLoading) {
    return <Skeleton />;
  }

  if (error) {
    return null;
  }

  // 根据 type 渲染不同的内容
  const renderContent = () => {
    if (type === 2 && digitalCurrencyQuery.data) {
      const data = digitalCurrencyQuery.data;
      const { label: statusLabel, className: statusClassName } = getStatusMeta(data.status);
      // 数字货币充值订单详情：使用 formatCurrencyDetail 格式化，4位小数
      const formattedAmount = formatCurrencyDetail(data.amount || '0', {
        currency: data.currency,
        kmbtMaximumFractionDigits: 4,
      });
      return (
        <>
          {/* 金额和状态部分 */}
          <div className="text-center mt-[25px]">
            <span className="block text-white text-[30px] font-semibold leading-none">
              {formattedAmount} {data.currency}
            </span>
            <div className="flex items-center justify-center gap-[12px] mt-[5px]">
              <span className={`text-[12px] font-medium leading-[12px] ${statusClassName}`}>
                {statusLabel}
              </span>
              {data.status === '2' && (
                <div className="tap-target-34 flex items-center justify-center cursor-pointer" onClick={handleRefresh}>
                  <Image
                    className={`w-[13px] h-[13px] transition-transform duration-500 ${isRefreshing ? 'animate-spin' : 'hover:rotate-90'}`}
                    src={confirmingIcon}
                    alt="confirming icon"
                    width={13}
                    height={13}
                  />
                </div>
              )}
            </div>
          </div>

          {/* 充值详情卡片 */}
          <div className="w-full h-[55px] bg-surface rounded-[8px] mt-[35px] flex items-center justify-between px-[15px]">
            <span className="text-tertiary text-[13px] font-medium">{t('history.depositAmount')}</span>
            <div className="flex items-center space-x-[10px]">
              <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center">
                {(() => {
                  const currencyIcon = getCurrencyIcon(data.currency || '', staticResource);
                  return currencyIcon ? (
                    <Image
                      src={currencyIcon}
                      alt={data.currency || ''}
                      width={26}
                      height={26}
                      className="w-[26px] h-[26px]"
                    />
                  ) : (
                    <div className="w-[26px] h-[26px] bg-tertiary rounded-full"></div>
                  );
                })()}
              </div>
              <span className="text-white text-[15px] font-semibold !ml-[10px]">
                {formattedAmount} {data.currency}
              </span>
            </div>
          </div>

          {/* 详情列表 */}
          <div className="w-full mt-[30px] space-y-[20px] px-[15px]">
            {data.src_currency && CRYPTO_ICONS[data.src_currency.toUpperCase()] && (
              <DetailRow
                label={t('amount')}
                value={
                  <div className="flex items-center gap-[6px]">
                    <Image
                      src={CRYPTO_ICONS[data.src_currency.toUpperCase()]}
                      alt={data.src_currency}
                      width={18}
                      height={18}
                      className="w-[18px] h-[18px]"
                    />
                    <span>{formatCurrencyDetail(data.src_amount || '0', { currency: data.src_currency, kmbtMaximumFractionDigits: 4 })}</span>
                    <span>{data.src_currency.toUpperCase()}</span>
                  </div>
                }
              />
            )}
            {data.blockchain && (
              <DetailRow
                label={t('address.network')}
                value={
                  <div className="flex items-center">
                    {data.chain_icon && <Image src={data.chain_icon} alt="chain icon" width={18} height={18} className="w-[18px] h-[18px] mr-[4px]" />}
                    <span>{data.blockchain}</span>
                  </div>
                }
              />
            )}
            {data.from && (
              <DetailRow
                label={t('from')}
                value={`${data.from.slice(0, 11)}......${data.from.slice(-8)}`}
                copyValue={data.from}
                showCopy={true}
              />
            )}
            {data.time && (
              <DetailRow
                label={t('history.creationTime')}
                value={formatSecondToDate(Number(data.time), 'DD/MM/YYYY HH:mm:ss')}
              />
            )}
            {data.status === '1' && data.finish_time && (
              <DetailRow
                label={t('history.arrivalTime')}
                value={formatSecondToDate(Number(data.finish_time), 'DD/MM/YYYY HH:mm:ss')}
              />
            )}
            {data.trans_hash && (
              <DetailRow
                label={t('history.hash')}
                value={`${data.trans_hash.slice(0, 11)}......${data.trans_hash.slice(-8)}`}
                copyValue={data.trans_hash}
                showCopy={true}
                explorerUrl={getBlockchainExplorerUrl(data.blockchain || '', data.trans_hash) || undefined}
              />
            )}
            {data.order_id && (
              <DetailRow
                label={t('history.orderId')}
                value={`${data.order_id.slice(0, 11)}......${data.order_id.slice(-8)}`}
                copyValue={data.order_id}
                showCopy={true}
              />
            )}
          </div>
        </>
      );
    } else if (type === 1 && cashDepositQuery.data) {
      const data = cashDepositQuery.data;
      const { label: statusLabel, className: statusClassName } = getStatusMeta(data.status);
      // 法币充值订单详情：使用 formatCurrencyDetail 格式化，2位小数
      const formattedAmount = formatCurrencyDetail(data.amount || '0', {
        currency: data.currency,
        kmbtMaximumFractionDigits: 2,
      });
      const currencyInfo = staticResource?.currency?.[data.currency as string];
      const currencySymbol = currencyInfo?.symbol || '';
      return (
        <>
          {/* 金额和状态部分 */}
          <div className="text-center mt-[25px]">
            <span className="text-white text-[30px] font-semibold leading-none">
              {currencySymbol} {formattedAmount}
            </span>
            <div className="flex items-center justify-center gap-[12px] mt-[5px]">
              <span className={`text-[12px] font-medium ${statusClassName}`}>
                {statusLabel}
              </span>
              {data.status === '2' && (
                <div className="tap-target-34 flex items-center justify-center cursor-pointer" onClick={handleRefresh}>
                  <Image
                    className={`w-[13px] h-[13px] transition-transform duration-500 ${isRefreshing ? 'animate-spin' : 'hover:rotate-90'}`}
                    src={confirmingIcon}
                    alt="confirming icon"
                    width={13}
                    height={13}
                  />
                </div>
              )}
            </div>
          </div>

          {/* 充值详情卡片 */}
          <div className="w-full h-[55px] bg-surface rounded-[8px] mt-[30px] flex items-center justify-between px-[15px]">
            <span className="text-tertiary text-[13px] font-medium">{t('history.depositAmount')}</span>
            <div className="flex items-center space-x-[10px]">
              <div className="rounded-full flex items-center justify-center">
                {(() => {
                  const currencyIcon = getCurrencyIcon(data.currency || '', staticResource);
                  return currencyIcon ? (
                    <Image
                      src={currencyIcon}
                      alt={data.currency || ''}
                      width={26}
                      height={26}
                      className="w-[26px] h-[26px]"
                    />
                  ) : (
                    <div className="w-5 h-5 bg-tertiary rounded-full"></div>
                  );
                })()}
              </div>
              <span className="text-white text-[15px] font-semibold">
                {currencySymbol}{formattedAmount}
              </span>
            </div>
          </div>

          {/* 详情列表 */}
          <div className="w-full mt-[30px] space-y-[20px] px-[15px]">
            {data.payment_mode && (
              <DetailRow
                label={t('history.paymentMethod')}
                value={data.payment_mode}
              />
            )}
            {data.time && (
              <DetailRow
                label={t('history.creationTime')}
                value={formatSecondToDate(Number(data.time), 'DD/MM/YYYY HH:mm:ss')}
              />
            )}
            {data.status === '1' && data.finish_time && (
              <DetailRow
                label={t('history.arrivalTime')}
                value={formatSecondToDate(Number(data.finish_time), 'DD/MM/YYYY HH:mm:ss')}
              />
            )}
            {data.order_id && (
              <DetailRow
                label={t('history.orderId')}
                value={`${data.order_id.slice(0, 11)}......${data.order_id.slice(-8)}`}
                copyValue={data.order_id}
                showCopy={true}
              />
            )}
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col items-center">
      {renderContent()}

      {/* 自定义刷新成功Toast - 距离底部177px */}
      {showRefreshToast && (
        <div className="fixed top-[357px] left-1/2 transform -translate-x-1/2 z-50">
          <div className="flex items-center justify-center w-[110px] h-[39px] bg-[#232a38] rounded-[8px]">
            <span className="text-secondary text-[12px] font-medium">{t('common.refreshed')}</span>
          </div>
        </div>
      )}
      {/* 法币详情才展示 */}
      {currency === CURRENCY_TYPE.INR ? <DelayTips time={delayTime} type="deposit" /> : null}
    </div>
  );
};

export default DepositDetails;